import { QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  PatientLogsVariables,
  PatientSearchVariables,
  ProfileCustomFields,
  UpdateCustomFieldsVariables,
  UpdatePersonalDataVariables,
} from 'models/profile';
import { getPatientLogsByGroup, getProfile, profileLoader, updateCustomField } from 'services/profile';
import { getPatientByName, updatePersonalData } from 'services/user';
import { queryKeys } from 'utils/query';
import { t } from 'i18next';
import { ObjectId } from '@extrahorizon/javascript-sdk';
import { useCustomMutation } from './useCustomMutation';

export const useProfile = (patientId?: string) =>
  useQuery({
    queryKey: [queryKeys.profileData, patientId],
    queryFn: () => getProfile(patientId),
    enabled: !!patientId,
  });

export const useUpdatePersonalData = ({ values, callback }: UpdatePersonalDataVariables) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => updatePersonalData(values),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [queryKeys.userData] });
      callback();
    },
  });
};

export const useUpdateCustomField = (variables: { customField: ProfileCustomFields; fieldLabel: string }) => {
  return useCustomMutation({
    fn: (values: UpdateCustomFieldsVariables) => updateCustomField({ ...values, customField: variables.customField }),
    onSuccess: { message: t('success_data_updated', { value: variables.fieldLabel }) },
  });
};

export const usePatientLogs = ({ groupId, profileId }: PatientLogsVariables) => {
  return useQuery({
    queryKey: [queryKeys.groupLogging, profileId, groupId],
    queryFn: () => getPatientLogsByGroup({ groupId, profileId }),
    enabled: !!profileId || !!groupId,
  });
};

export const useSearchPatientName = ({ searchValue }: PatientSearchVariables) => {
  return useQuery({
    queryKey: [queryKeys.patientName, searchValue],
    queryFn: () => getPatientByName({ searchValue }),
    enabled: !!searchValue?.trim(),
  });
};

export const getProfileByIdQuery = (queryClient: QueryClient, id: string) => {
  return queryClient.fetchQuery({
    queryKey: [queryKeys.profiles, id],
    // Profile Loader combines single profile request into a batched call
    queryFn: () => profileLoader.load(id),
    staleTime: 1000 * 60 * 30, // 30 min
  });
};

export const useProfileQuery = (id: ObjectId) => {
  return useQuery({
    queryKey: [queryKeys.profiles, id],
    // Profile Loader combines single profile request into a batched call
    queryFn: () => profileLoader.load(id),
    staleTime: 1000 * 60 * 30, // 30 min
  });
};
